import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded } from "stores/sessionGuard";

/**
 * The three RBAC tiers, in ascending authority. Mirrors the API's
 * `_shared/rbac-tier.ts` — that file is the enforcement point, this is a
 * read-only mirror used to label the picker.
 */
export const TIER_CHOICES = ["viewer", "operator", "admin"] as const;
export type Tier = (typeof TIER_CHOICES)[number];

/** What each tier actually lets a holder do, in the admin's own words. */
export const TIER_DESCRIPTIONS: Record<Tier, string> = {
  viewer: "Read-only. Can see pages but cannot change anything.",
  operator:
    "Routine operational work — deploys, start/stop, work items, links and most day-to-day writes.",
  admin: "Full control, including users, roles, API keys, hosts and secrets.",
};

export interface RoleRow {
  id: string;
  name: string;
  description?: string | null;
  tier?: Tier | null;
  isSystem?: boolean | null;
}

export interface RoleFormData {
  name: string;
  description: string;
  tier: Tier;
}

const EMPTY_FORM: RoleFormData = { name: "", description: "", tier: "viewer" };

/**
 * Longest assignable role name.
 *
 * `roles.name` permits 100 characters, but a role is only useful once it is on
 * a user, and `users.role` is NVARCHAR(50). The narrower column is the real
 * limit; allowing 100 here just defers the failure to the moment somebody tries
 * to assign it.
 */
export const MAX_ROLE_NAME = 50;

/**
 * Names the API's built-in ROLE_TIER map already answers for. A custom role may
 * not reuse one — the server refuses with a 400, and we say so before the admin
 * fills in the rest of the form. Kept in sync with ROLE_TIER.
 */
export const RESERVED_ROLE_NAMES = [
  "admin",
  "owner",
  "superadmin",
  "controller",
  "project_manager",
  "manager",
  "operator",
  "staff",
  "lead",
  "ap_clerk",
  "ar_clerk",
  "time_keeper",
  "viewer",
];

interface RolesApi {
  create: (data: object) => Promise<RoleRow>;
  patch: (id: string, data: object) => Promise<RoleRow>;
  remove: (id: string) => Promise<RoleRow>;
}

/**
 * Administration → Roles.
 *
 * Roles are DATA, not a hardcoded list: a business defines the roles it
 * actually has, and each one declares the RBAC tier it confers. This store is
 * the write path for that table.
 *
 * Writes go through the RAW Feathers service, not the ServiceStore, for the
 * same reason UserManagementStore does: the base ServiceStore swallows server
 * errors and resolves `undefined`, which would report a refused system-role
 * delete or a reserved-name rejection as SUCCESS. Every rule here is enforced
 * server-side (`requireUserAdmin`, `preventSystemRoleDelete`,
 * `preventSystemFlagChange`, `preventSystemTierChange`); the UI mirrors them so
 * the reason is visible before the request, never instead of it.
 */
export default class RolesStore {
  rootStore: RootStore;

  formData: RoleFormData = { ...EMPTY_FORM };
  editingId: string | null = null;
  /** Why the role dialog's save was refused — its route throws this, so it
   *  shows in the dialog's footer. */
  saveError: string | null = null;
  /** Why a Delete on the list was refused — shown above the list. */
  removeError: string | null = null;

  /**
   * Why the role list could not be loaded, or null after a successful load.
   * The ServiceStore swallows a failed `find` (resolves null), so without this
   * the page said "No roles yet." while `roles` was failing.
   */
  loadError: string | null = null;

  /**
   * Whether the name field has been interacted with (typed into or blurred)
   * or a save was attempted. `nameError` stays the validity source — it gates
   * Save — but the dialog only SHOWS it once this is true, so a fresh Create
   * dialog does not open with the field already red.
   */
  nameTouched = false;
  isSaving = false;
  search = "";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private get rolesService(): ServiceStoreType | null {
    return (
      (this.rootStore as unknown as { rolesStore?: ServiceStoreType })
        .rolesStore ?? null
    );
  }

  private get rolesApi(): RolesApi | null {
    const app = this.rootStore.app as
      | { service?: (path: string) => RolesApi }
      | undefined;
    return app?.service?.("roles") ?? null;
  }

  /** Every role the service holds, ignoring the search box. */
  get allRows(): RoleRow[] {
    return (this.rolesService?.entities ?? []) as unknown as RoleRow[];
  }

  get rows(): RoleRow[] {
    const all = this.allRows;
    const q = this.search.trim().toLowerCase();
    const matched = q
      ? all.filter(
          (r) =>
            (r.name ?? "").toLowerCase().includes(q) ||
            (r.description ?? "").toLowerCase().includes(q)
        )
      : all;

    // Built-in roles first, then custom ones alphabetically — the familiar
    // names stay put as a business adds its own.
    return [...matched].sort((a, b) => {
      const rank = Number(!!b.isSystem) - Number(!!a.isSystem);
      return rank !== 0 ? rank : (a.name ?? "").localeCompare(b.name ?? "");
    });
  }

  get isLoading(): boolean {
    return this.rolesService?.isFindPending === true;
  }

  setSearch(value: string) {
    this.search = value;
  }

  setFormField<K extends keyof RoleFormData>(field: K, value: RoleFormData[K]) {
    this.formData[field] = value;
    if (field === "name") this.nameTouched = true;
    this.saveError = null;
  }

  /** The name field lost focus — from now on its error is shown. */
  touchName() {
    this.nameTouched = true;
  }

  /** The error the name field should DISPLAY: `nameError`, once touched. */
  get visibleNameError(): string | null {
    return this.nameTouched ? this.nameError : null;
  }

  /** Open the dialog empty, for a new custom role. */
  beginCreate() {
    this.editingId = null;
    this.formData = { ...EMPTY_FORM };
    this.saveError = null;
    this.nameTouched = false;
  }

  /** Open the dialog on an existing role. */
  beginEdit(role: RoleRow) {
    this.editingId = role.id;
    this.formData = {
      name: role.name ?? "",
      description: role.description ?? "",
      tier: (role.tier as Tier) ?? "viewer",
    };
    this.saveError = null;
    this.nameTouched = false;
  }

  get editingRole(): RoleRow | null {
    if (!this.editingId) return null;
    return this.rows.find((r) => r.id === this.editingId) ?? null;
  }

  /**
   * True while editing one of the seeded roles. Its name and tier are fixed
   * server-side; only the description is writable.
   */
  get isEditingSystemRole(): boolean {
    return this.editingRole?.isSystem === true;
  }

  get nameError(): string | null {
    const name = this.formData.name.trim();
    if (!name) return "A role needs a name.";
    if (this.isEditingSystemRole) return null;

    if (RESERVED_ROLE_NAMES.includes(name.toLowerCase())) {
      return `"${name}" is a built-in role name — choose a different one.`;
    }

    // `allRows`, NOT `rows`. `rows` is search-filtered, so typing a search that
    // hides a clashing role made this check pass and let the duplicate through.
    // The API enforces uniqueness too; this is only here to say so before the
    // admin fills in the rest of the form.
    const clash = this.allRows.find(
      (r) =>
        r.id !== this.editingId &&
        (r.name ?? "").trim().toLowerCase() === name.toLowerCase()
    );
    if (clash) return `A role named "${name}" already exists.`;

    // `users.role` is NVARCHAR(50) (20260408000004) while `roles.name` allows
    // 100. A longer role saves fine and then cannot be ASSIGNED to anybody --
    // it appears in the picker and fails validation on submit. Refuse it here.
    if (name.length > MAX_ROLE_NAME) {
      return `Keep the name to ${MAX_ROLE_NAME} characters or fewer.`;
    }

    return null;
  }

  get isFormValid(): boolean {
    return this.nameError === null;
  }

  /**
   * Create or update. Resolves true on success; the caller keeps the dialog
   * open on false so `saveError` stays readable.
   */
  async save(): Promise<boolean> {
    this.nameTouched = true;
    if (!this.isFormValid) return false;

    const api = this.rolesApi;
    if (!api) {
      runInAction(() => {
        this.saveError = "Roles service is unavailable.";
      });
      return false;
    }

    runInAction(() => {
      this.isSaving = true;
      this.saveError = null;
    });

    try {
      const description = this.formData.description.trim();

      if (this.editingId) {
        // A system role's name and tier are refused by the API; sending them
        // unchanged would still be refused, so send only what may change.
        const patch = this.isEditingSystemRole
          ? { description }
          : {
              name: this.formData.name.trim(),
              description,
              tier: this.formData.tier,
            };
        await api.patch(this.editingId, patch);
      } else {
        await api.create({
          name: this.formData.name.trim(),
          description,
          tier: this.formData.tier,
          defaultPermissions: [],
        });
      }

      await this.refresh();
      runInAction(() => {
        this.isSaving = false;
        this.editingId = null;
        this.formData = { ...EMPTY_FORM };
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.isSaving = false;
        if (!isSessionEnded(error)) this.saveError = errorMessage(error);
      });
      return false;
    }
  }

  /** Delete a custom role. System roles are refused server-side. */
  async removeRole(id: string): Promise<boolean> {
    const api = this.rolesApi;
    if (!api) return false;
    this.removeError = null;
    try {
      await api.remove(id);
      await this.refresh();
      return true;
    } catch (error) {
      runInAction(() => {
        if (!isSessionEnded(error)) this.removeError = errorMessage(error);
      });
      return false;
    }
  }

  /**
   * Load (or reload) the role list. Never rejects: a failure lands on
   * `loadError`, which the body renders as an error with Retry instead of the
   * "No roles yet." empty state.
   */
  async load(): Promise<void> {
    try {
      await findOrThrow(this.rolesService, { query: { $limit: 200 } });
      runInAction(() => {
        this.loadError = null;
      });
    } catch (error) {
      runInAction(() => {
        if (!isSessionEnded(error)) this.loadError = loadErrorMessage(error, "The roles could not be loaded.");
      });
    }
  }

  /** Reload after a write. A failed reload shows as `loadError`, not silence. */
  async refresh(): Promise<void> {
    await this.load();
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.removeError = null;
    this.formData = { ...EMPTY_FORM };
    this.editingId = null;
    this.saveError = null;
    this.loadError = null;
    this.nameTouched = false;
    this.isSaving = false;
    this.search = "";
  }
}

/** Feathers errors carry the useful text on `message`; anything else is opaque. */
function errorMessage(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    const msg = (error as { message?: unknown }).message;
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  return "Could not save the role.";
}
